import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Checkbox } from '../ui/checkbox';
import { Progress } from '../ui/progress';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { FileText, FilePlus, Upload, Download, Edit, Copy, Eye, Save, Plus, BarChart3, TrendingUp, Check, Stamp, Share2, QrCode, Printer, Code, FileSpreadsheet, FileImage } from 'lucide-react';
import { TEMPLATE_CATEGORIES, STAMP_TYPES, RECIPIENTS } from './constants';
import TemplatesTab from './TemplatesTab';
import StampsTab from './StampsTab';
import useGestionModeles from './useGestionModeles';

export default function GestionModelesAvancee({ getAuthHeader, userRole = 'super_admin' }) {
  const ctx = useGestionModeles({ getAuthHeader, userRole });
  const {
    templates,
    stamps,
    activeTab,
    setActiveTab,
    selectedCategory,
    setSelectedCategory,
    selectedStatus,
    setSelectedStatus,
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    showEditorModal,
    setShowEditorModal,
    showPreviewModal,
    setShowPreviewModal,
    showShareModal,
    setShowShareModal,
    showStampModal,
    setShowStampModal,
    showStatsModal,
    setShowStatsModal,
    selectedTemplate,
    setSelectedTemplate,
    editMode,
    stampEditMode,
    editorContent,
    setEditorContent,
    editorForm,
    setEditorForm,
    stampForm,
    setStampForm,
    importProgress,
    isImporting,
    fileInputRef,
    stampInputRef,
    stats,
    filteredTemplates,
    generateSignatureBlock,
    openEditor,
    saveTemplate,
    changeStatus,
    deleteTemplate,
    exportToPDF,
    exportToHTML,
    printTemplate,
    resetStampForm,
    openStampModal,
    openEditStamp,
    handleStampImageImport,
    toggleStampCategory,
    saveStamp,
    deleteStamp,
    shareTemplate,
    getPreviewContent,
    handleFileImport,
  } = ctx;

    return (
    <div className="space-y-6" data-testid="gestion-modeles">
      {/* En-tête */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
              <FileText className="h-6 w-6 text-white" />
            </div>
            Gestion des Modèles de Documents
          </h2>
          <p className="text-gray-500 mt-1">
            Créez, éditez et partagez vos modèles avec bloc de signature et QR code
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileInputRef} type="file"
            accept=".html,.htm,.docx,.xlsx,.xls,.csv,.txt,.png,.jpg,.jpeg,.gif,.webp,.bmp,.svg"
            onChange={handleFileImport} className="hidden" />
          <Button variant="outline" onClick={() => setShowStatsModal(true)} data-testid="stats-btn">
            <BarChart3 className="h-4 w-4 mr-2" />Statistiques
          </Button>
          <Button variant="outline" onClick={() => setActiveTab('stamps')} data-testid="stamps-tab-btn">
            <Stamp className="h-4 w-4 mr-2" />Tampons ({stamps.length})
          </Button>
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isImporting} data-testid="import-btn">
            <Upload className="h-4 w-4 mr-2" />{isImporting ? 'Import...' : 'Importer'}
          </Button>
          <Button onClick={() => openEditor(null, 'create')} className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700" data-testid="new-template-btn">
            <Plus className="h-4 w-4 mr-2" />Nouveau modèle
          </Button>
        </div>
      </div>

      {/* Barre de progression d'import */}
      {isImporting && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">Import en cours...</p>
                <Progress value={importProgress} className="h-2 mt-2" />
              </div>
              <span className="text-sm font-bold text-blue-700">{importProgress}%</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Formats supportés */}
      <Card className="bg-gradient-to-r from-gray-50 to-white border-gray-200">
        <CardContent className="py-3">
          <div className="flex items-center gap-4 text-sm">
            <span className="font-medium text-gray-700">Formats supportés :</span>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="flex items-center gap-1">
                <FileText className="h-3 w-3 text-blue-600" />Word (.docx)
              </Badge>
              <Badge variant="outline" className="flex items-center gap-1">
                <FileSpreadsheet className="h-3 w-3 text-green-600" />Excel (.xlsx, .xls)
              </Badge>
              <Badge variant="outline" className="flex items-center gap-1">
                <FileImage className="h-3 w-3 text-purple-600" />Images (PNG, JPG, etc.)
              </Badge>
              <Badge variant="outline" className="flex items-center gap-1">
                <Code className="h-3 w-3 text-orange-600" />HTML, CSV, TXT
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total modèles', value: stats.total, icon: FileText, colors: 'blue' },
          { label: 'Actifs', value: stats.active, icon: Check, colors: 'green' },
          { label: 'Utilisations', value: stats.totalUsage, icon: TrendingUp, colors: 'purple' },
          { label: 'Tampons', value: stamps.length, icon: Stamp, colors: 'orange' },
        ].map(({ label, value, icon: Icon, colors }) => (
          <Card key={label} className={`bg-gradient-to-br from-${colors}-50 to-white border-${colors}-200`}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg bg-${colors}-100 flex items-center justify-center`}>
                  <Icon className={`h-5 w-5 text-${colors}-600`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold text-${colors}-700`}>{value}</p>
                  <p className={`text-xs text-${colors}-600`}>{label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Onglets */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="templates" className="flex items-center gap-2" data-testid="templates-tab-trigger">
            <FileText className="h-4 w-4" />Modèles
          </TabsTrigger>
          <TabsTrigger value="stamps" className="flex items-center gap-2" data-testid="stamps-tab-trigger">
            <Stamp className="h-4 w-4" />Collection Tampons
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates">
          <TemplatesTab
            filteredTemplates={filteredTemplates}
            searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory}
            selectedStatus={selectedStatus} setSelectedStatus={setSelectedStatus}
            sortBy={sortBy} setSortBy={setSortBy}
            sortOrder={sortOrder} setSortOrder={setSortOrder}
            stamps={stamps}
            openEditor={openEditor}
            exportToPDF={exportToPDF} exportToHTML={exportToHTML} printTemplate={printTemplate}
            changeStatus={changeStatus} deleteTemplate={deleteTemplate}
            setSelectedTemplate={setSelectedTemplate}
            setShowPreviewModal={setShowPreviewModal} setShowShareModal={setShowShareModal}
          />
        </TabsContent>

        <TabsContent value="stamps">
          <StampsTab
            stamps={stamps}
            openEditStamp={openEditStamp}
            deleteStamp={deleteStamp}
            openStampModal={openStampModal}
          />
        </TabsContent>
      </Tabs>

      {/* ============ MODALS ============ */}

      {/* Modal Éditeur */}
      <Dialog open={showEditorModal} onOpenChange={setShowEditorModal}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editMode === 'create' ? <FilePlus className="h-5 w-5 text-green-600" /> :
               editMode === 'duplicate' ? <Copy className="h-5 w-5 text-blue-600" /> :
               <Edit className="h-5 w-5 text-orange-600" />}
              {editMode === 'create' ? 'Nouveau modèle' : 
               editMode === 'duplicate' ? 'Dupliquer le modèle' : 'Modifier le modèle'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 h-[70vh]">
            <div className="space-y-4 overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom du modèle *</Label>
                  <Input value={editorForm.name} onChange={(e) => setEditorForm({...editorForm, name: e.target.value})} placeholder="Ex: Déclaration de dommage" className="mt-1" />
                </div>
                <div>
                  <Label>Catégorie</Label>
                  <Select value={editorForm.category} onValueChange={(v) => setEditorForm({...editorForm, category: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TEMPLATE_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          <span className="flex items-center gap-2"><cat.icon className="h-4 w-4" />{cat.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Input value={editorForm.description} onChange={(e) => setEditorForm({...editorForm, description: e.target.value})} placeholder="Description du modèle" className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-black font-semibold">Tampon associé</Label>
                  <Select value={editorForm.stampId || 'none'} onValueChange={(v) => setEditorForm({...editorForm, stampId: v === 'none' ? null : v})}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Aucun tampon" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun tampon</SelectItem>
                      {stamps.map((stamp) => (
                        <SelectItem key={stamp.id} value={stamp.id}>
                          <span className="flex items-center gap-2 text-black font-medium">
                            <Stamp className="h-4 w-4 text-orange-600" />{stamp.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg w-full">
                    <Checkbox id="includeSignature" checked={editorForm.includeSignatureBlock}
                      onCheckedChange={(checked) => setEditorForm({...editorForm, includeSignatureBlock: checked})} />
                    <Label htmlFor="includeSignature" className="cursor-pointer flex items-center gap-2">
                      <QrCode className="h-4 w-4 text-blue-600" />Inclure bloc signature + QR
                    </Label>
                  </div>
                </div>
              </div>
              <div>
                <Label>Partager avec</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {RECIPIENTS.map((recipient) => (
                    <div key={recipient.id} className="flex items-center gap-2">
                      <Checkbox id={`share-${recipient.id}`}
                        checked={editorForm.sharedWith.includes(recipient.id)}
                        onCheckedChange={(checked) => {
                          setEditorForm({
                            ...editorForm,
                            sharedWith: checked
                              ? [...editorForm.sharedWith, recipient.id]
                              : editorForm.sharedWith.filter(r => r !== recipient.id)
                          });
                        }} />
                      <Label htmlFor={`share-${recipient.id}`} className="cursor-pointer flex items-center gap-1">
                        <recipient.icon className="h-4 w-4" />{recipient.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-1">
                <Label>Contenu HTML *</Label>
                <Textarea value={editorContent} onChange={(e) => setEditorContent(e.target.value)}
                  placeholder="<div>Contenu de votre modèle...</div>"
                  className="mt-1 font-mono text-sm h-64 resize-none" />
                <p className="text-xs text-gray-400 mt-1">
                  Variables disponibles: {'{{nom}}'}, {'{{prenom}}'}, {'{{date}}'}, {'{{reference}}'}, etc.
                </p>
              </div>
            </div>
            <div className="border rounded-lg overflow-hidden bg-white">
              <div className="bg-gray-100 px-4 py-2 border-b flex items-center justify-between">
                <span className="text-sm font-medium flex items-center gap-2"><Eye className="h-4 w-4" />Aperçu en direct</span>
                <Badge className="bg-green-600 text-white text-xs font-semibold">Live Preview</Badge>
              </div>
              <div className="p-4 h-full overflow-auto"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(editorContent.replace('{{BLOC_SIGNATURE}}', editorForm.includeSignatureBlock ? generateSignatureBlock('preview') : '')) }} />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowEditorModal(false)}>Annuler</Button>
            <Button onClick={saveTemplate} className="bg-blue-600 hover:bg-blue-700">
              <Save className="h-4 w-4 mr-2" />{editMode === 'edit' ? 'Mettre à jour' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Prévisualisation */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-600" />{selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[70vh] border rounded-lg bg-white p-6">
            {selectedTemplate && <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(getPreviewContent(selectedTemplate)) }} />}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>Fermer</Button>
            <Button variant="outline" onClick={() => selectedTemplate && printTemplate(selectedTemplate)}>
              <Printer className="h-4 w-4 mr-2" />Imprimer
            </Button>
            <Button variant="outline" onClick={() => selectedTemplate && exportToHTML(selectedTemplate)}>
              <Code className="h-4 w-4 mr-2" />HTML
            </Button>
            <Button onClick={() => selectedTemplate && exportToPDF(selectedTemplate)} className="bg-red-600 hover:bg-red-700">
              <Download className="h-4 w-4 mr-2" />PDF
            </Button>
            <Button onClick={() => { setShowPreviewModal(false); openEditor(selectedTemplate, 'edit'); }} className="bg-blue-600 hover:bg-blue-700">
              <Edit className="h-4 w-4 mr-2" />Modifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Partage */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5 text-green-600" />Partager le modèle
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-gray-600">Sélectionnez les espaces avec lesquels partager ce modèle :</p>
            {RECIPIENTS.map((recipient) => (
              <div key={recipient.id}
                className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                onClick={() => {
                  if (selectedTemplate) {
                    const newSharedWith = selectedTemplate.sharedWith?.includes(recipient.id)
                      ? selectedTemplate.sharedWith.filter(r => r !== recipient.id)
                      : [...(selectedTemplate.sharedWith || []), recipient.id];
                    shareTemplate(selectedTemplate.id, newSharedWith);
                    setSelectedTemplate({...selectedTemplate, sharedWith: newSharedWith});
                  }
                }}>
                <Checkbox checked={selectedTemplate?.sharedWith?.includes(recipient.id)} />
                <div className={`w-10 h-10 rounded-lg ${recipient.color} flex items-center justify-center`}>
                  <recipient.icon className="h-5 w-5 text-white" />
                </div>
                <span className="font-medium">{recipient.label}</span>
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={() => setShowShareModal(false)}>Fermer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Tampon */}
      <Dialog open={showStampModal} onOpenChange={(open) => { setShowStampModal(open); if (!open) resetStampForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stamp className="h-5 w-5 text-purple-600" />
              {stampEditMode === 'edit' ? 'Modifier le tampon' : 'Ajouter un tampon'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Nom du tampon *</Label>
              <Input placeholder="Ex: Tampon Officiel" className="mt-1" value={stampForm.name}
                onChange={(e) => setStampForm({...stampForm, name: e.target.value})} />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={stampForm.type} onValueChange={(v) => setStampForm({...stampForm, type: v})}>
                <SelectTrigger className="mt-1 text-black"><SelectValue placeholder="Sélectionner un type" /></SelectTrigger>
                <SelectContent>
                  {STAMP_TYPES.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      <span className="flex items-center gap-2 text-black font-medium">
                        <div className={`w-3 h-3 rounded-full ${type.color}`}></div>{type.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Image du tampon</Label>
              <input ref={stampInputRef} type="file" accept="image/*" className="hidden" onChange={handleStampImageImport} />
              <div onClick={() => stampInputRef.current?.click()}
                className="mt-2 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors">
                {stampForm.imagePreview ? (
                  <div className="space-y-2">
                    <img src={stampForm.imagePreview} alt="Aperçu tampon" className="w-24 h-24 mx-auto object-contain rounded-full border-2 border-purple-200" />
                    <p className="text-sm text-purple-600">Cliquez pour changer l'image</p>
                  </div>
                ) : (
                  <>
                    <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Cliquez pour importer</p>
                    <p className="text-xs text-gray-400">PNG transparent recommandé</p>
                  </>
                )}
              </div>
            </div>
            <div>
              <Label>Attribuer aux catégories</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {TEMPLATE_CATEGORIES.map((cat) => (
                  <Badge key={cat.id}
                    variant={stampForm.assignedTo.includes(cat.id) ? "default" : "outline"}
                    className={`cursor-pointer transition-colors ${stampForm.assignedTo.includes(cat.id) ? `${cat.color} text-white` : 'hover:bg-gray-100'}`}
                    onClick={() => toggleStampCategory(cat.id)}>
                    <cat.icon className="h-3 w-3 mr-1" />{cat.label}
                  </Badge>
                ))}
              </div>
              {stampForm.assignedTo.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">{stampForm.assignedTo.length} catégorie(s) sélectionnée(s)</p>
              )}
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => { setShowStampModal(false); resetStampForm(); }}>Annuler</Button>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={saveStamp}>
              <Save className="h-4 w-4 mr-2" />{stampEditMode === 'edit' ? 'Modifier' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Statistiques */}
      <Dialog open={showStatsModal} onOpenChange={setShowStatsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-purple-600" />Statistiques des modèles
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-6">
            <div>
              <h4 className="font-medium mb-3">Répartition par catégorie</h4>
              <div className="space-y-2">
                {stats.byCategory.filter(c => c.count > 0).map((cat) => (
                  <div key={cat.id} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded ${cat.color} flex items-center justify-center`}>
                      <cat.icon className="h-4 w-4 text-white" />
                    </div>
                    <span className="flex-1">{cat.label}</span>
                    <Badge className="bg-gray-700 text-white font-semibold">{cat.count}</Badge>
                    <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full ${cat.color}`} style={{ width: `${(cat.count / stats.total) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-3">Modèles les plus utilisés</h4>
              <div className="space-y-2">
                {stats.mostUsed.map((template, index) => (
                  <div key={template.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-medium">{index + 1}</span>
                    <span className="flex-1 truncate">{template.name}</span>
                    <Badge className="bg-purple-700 text-white font-semibold">{template.usageCount} utilisations</Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowStatsModal(false)}>Fermer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
